import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  buildCheckCsvColumns,
  GetChecksRequestDTO,
  GetChecksResponseDTO,
  MODULE,
  PDF_SCOPE,
  PdfScope,
  ScheduleCheckColumnId,
  UpdateChecksRequestDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useLocation } from 'react-router-dom';

import CheckDrawer from '@/components/checks/check-drawer';
import { ShareDialog } from '@/components/common/share-dialog';
import { FeedbackPrompt } from '@/components/feedback/feedback-prompt';
import DownloadCsvIcon from '@/components/icons/download-csv';
import LinkViewIcon from '@/components/icons/link-view';
import TagIcon from '@/components/icons/tag';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useFeedbackPrompt } from '@/hooks/use-feedback-prompt';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { tagKeys } from '@/queries/assets';
import { checkKeys, getChecksInfiniteQueryOptions } from '@/queries/checks';
import { buildFilterFields } from '@/queries/column-filter';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { buildPdfExportJob, exportColumnIdsIn, overLimitExportError, RegisterExportQuery } from '@/utils/register-pdf-export';
import { getShareTargets } from '@/utils/share-targets';

import { TopbarActions } from '../topbar-actions';
import { BulkUpdateCheckTagsDialog } from './bulk-update-check-tags-dialog';
import { ChecksEmptyDisplay, ChecksNoFiltersMatchDisplay, ChecksNoResultsDisplay, ScheduleChecksTable } from './table';
import { createCheckTableColumns, FILTER_FIELD_MAP, groupOptions } from './table-meta';

const ScheduleChecksPage = () => {
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { canManageSchedules, canExportCsv } = usePermissions();
  const { setModuleFilters } = useModulesStore();
  const locationState = useLocation().state as { checkId?: string } | undefined;
  const checkIdFromLocation = locationState?.checkId;

  const { triggerForScheduleCheck, promptProps: feedbackPromptProps } = useFeedbackPrompt('schedule-checks');

  const { data: units, isLoading: isLoadingUnits } = useQuery(getUnitsQueryOptions());

  const tableColumns = useMemo(() => createCheckTableColumns({ units: units }), [units]);

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.ScheduleChecks, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: tableColumns,
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openChecksTagsUpdate, setOpenChecksTagsUpdate] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(checkIdFromLocation ?? null);
  const [isCheckDrawerOpen, setIsCheckDrawerOpen] = useState(!!checkIdFromLocation);

  useEffect(() => {
    if (locationState) {
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSearch = useDebounce(search, 500);

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  const {
    data: checksData,
    fetchNextPage: fetchNextChecksPage,
    hasNextPage: hasNextChecksPage,
    isFetching: isFetchingChecks,
    isFetchingNextPage: isFetchingNextChecksPage,
    error: checksError,
  } = useInfiniteQuery(
    getChecksInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetChecksRequestDTO['groupBy']>['field'], direction: sorting.direction || 'asc' }
        : undefined,
      orderBy: sorting.key ? { field: sorting.key as NonNullable<GetChecksRequestDTO['orderBy']>['field'], direction: sorting.direction ?? 'asc' } : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedChecks = useMemo(() => {
    return (incomingData: typeof checksData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const checks = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => checks[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedChecks = useMemo(() => getSelectedChecks(checksData), [checksData, getSelectedChecks]);

  const { mutateAsync: updateChecks, isPending: isUpdatingChecks } = useMutation({
    mutationFn: async ({ checkIds, check }: UpdateChecksRequestDTO) => await ApiService.UpdateChecks({ checkIds, check }),
    onSuccess: () => {
      toast.success('Schedule checks updated successfully');
      setOpenChecksTagsUpdate(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: checkKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleUpdateChecksTags = async (newTags: string[], resetForm: () => void) => {
    if (!checksData?.pages) return;
    const toUpdate = getSelectedChecks(checksData);
    await updateChecks({
      checkIds: toUpdate.map((check) => check.id),
      check: { tags: newTags },
    });
    resetForm();
  };

  const { shareUrl: shareTarget, appUrl } = getShareTargets('schedule-checks');

  function buildScheduleChecksExportRequest(): RegisterExportQuery<'schedule-checks'> {
    const selectedItems = getSelectedChecks(checksData);
    return {
      queryType: 'schedule-checks',
      filters: { fields: { id: selectedItems.map((check) => check.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetChecksRequestDTO['groupBy']>['field'], direction: sorting.direction || 'asc' }
        : undefined,
      orderBy: sorting.key ? { field: sorting.key as NonNullable<GetChecksRequestDTO['orderBy']>['field'], direction: sorting.direction ?? 'asc' } : undefined,
      columns: exportColumnIdsIn(columnOrder, new Set(Object.keys(buildCheckCsvColumns(units ?? [])))),
    };
  }

  function handleCsvExport() {
    if (!checksData?.pages) return;

    openExportTab({
      kind: 'csv',
      request: { ...buildScheduleChecksExportRequest(), filename: buildCsvExportFilename('schedule-checks') },
    });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!checksData?.pages) return;
    const selectedChecksForExport = getSelectedChecks(checksData);
    const overLimitError = overLimitExportError(scope, selectedChecksForExport, {
      composeScope: PDF_SCOPE.REGISTER_CHECKS_AND_FORM_RECORDS,
      parentNoun: 'checks',
      composeRecordNoun: 'check and form',
      linkedRecordId: (check) => check.scheduleFormId,
    });
    if (overLimitError) {
      toast.error(overLimitError);
      return;
    }
    openExportTab(buildPdfExportJob({ scope, recordType: 'check', records: selectedChecksForExport, register: buildScheduleChecksExportRequest() }));
  }

  useEffect(() => {
    if (checksError) {
      toast.error(checksError.message);
    }
  }, [toast, checksError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextChecksPage,
    hasNextPage: hasNextChecksPage,
    isFetching: isFetchingChecks,
    isError: !!checksError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => checksData?.pages.flatMap((page) => page.data) ?? [], [checksData]);

  const handleRowClick = useCallback((check: GetChecksResponseDTO['data'][number]) => {
    setSelectedCheckId(check.id);
    setIsCheckDrawerOpen(true);
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedChecks.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Schedule Checks</TablePageTitle>
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.ScheduleChecks, [])}
                    />
                  )}
                </div>

                <div className="flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) => updatePageConfig({ table: { grouping: value ? [value] : [] } })}
                    />
                    <ColumnsDialog<GetChecksResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      defaultSectionLabel="Schedule Check"
                      onOpenChange={setOpenColumnsDialog}
                      columns={tableColumns}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(newColumnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: newColumnOrder as Array<{ id: ScheduleCheckColumnId; active: boolean }> },
                        })
                      }
                      disabled={isLoadingPageConfig || isLoadingUnits}
                    />
                    {shareTarget && <TablePageHeaderActionButton onClick={() => setShareUrl(shareTarget)} icon={<ShareIcon />} label="Share Link" />}
                    {appUrl && <TablePageHeaderActionButton onClick={() => window.open(appUrl, '_blank')} icon={<LinkViewIcon />} label="Open Mechanic App" />}
                  </div>
                </div>
              </>
            )}

            {selectedChecks.length > 0 && (
              <div className="flex min-h-10 items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedChecks.length} totalCount={checksData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} onClick={handleCsvExport} label="Download CSV" />}
                {canExportCsv && (
                  <DownloadPdfMenu
                    recordNoun="check"
                    canExportRegister={canExportCsv}
                    extraItems={[
                      {
                        scope: PDF_SCOPE.REGISTER_CHECKS_AND_FORM_RECORDS,
                        label: 'Register, check records and form records',
                        help: 'Download the summary sheet, then each selected check immediately followed by its linked form record, bound into one PDF.',
                      },
                    ]}
                    onSelect={handlePdfScopeSelect}
                  />
                )}
                {canManageSchedules && <TablePageHeaderActionButton icon={<TagIcon />} label="Edit Tags" onClick={() => setOpenChecksTagsUpdate(true)} />}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <ScheduleChecksTable
          data={flattenedData}
          units={units ?? []}
          loading={(isFetchingChecks && !isFetchingNextChecksPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <ChecksNoResultsDisplay />
            ) : filters && filters.length > 0 ? (
              <ChecksNoFiltersMatchDisplay removeColumnFilters={() => setModuleFilters(MODULE.ScheduleChecks, [])} />
            ) : (
              <ChecksEmptyDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(newFilters) => setModuleFilters(MODULE.ScheduleChecks, newFilters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
          onRowClick={handleRowClick}
        />
        {hasNextChecksPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextChecksPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}

      <CheckDrawer checkId={selectedCheckId} open={isCheckDrawerOpen} onOpenChange={setIsCheckDrawerOpen} onCloseSuccess={triggerForScheduleCheck} />

      <BulkUpdateCheckTagsDialog
        open={openChecksTagsUpdate}
        onOpenChange={setOpenChecksTagsUpdate}
        callback={handleUpdateChecksTags}
        loading={isUpdatingChecks}
      />

      <FeedbackPrompt {...feedbackPromptProps} />
    </div>
  );
};

export default ScheduleChecksPage;
